import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, UserCog, AlertTriangle, UserPlus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import UserRoleField from "@/components/users/UserRoleField";
import {
  getAssignableWorkspaceRoleOptions,
  getWorkspaceRoleBadgeClass,
  getWorkspaceRoleLabel,
} from "@/lib/userRoles";
import {
  canShowDeleteButton,
  getUserRoleHint,
  resolveUserDeleteBlockReason,
} from "@/lib/userDeleteEligibility";
import { filterInternalBntAdminUsers } from "@/lib/userClassification";

const EMPTY_RESELLER = {
  requires_invoice: false,
  datos_generales: "",
  factura_rfc: "",
  factura_razon_social: "",
  factura_email: "",
  factura_codigo_postal: "",
};

function normalizeReseller(r) {
  if (!r || typeof r !== "object") return { ...EMPTY_RESELLER };
  return {
    requires_invoice: !!r.requires_invoice,
    datos_generales: r.datos_generales || "",
    factura_rfc: r.factura_rfc || "",
    factura_razon_social: r.factura_razon_social || "",
    factura_email: r.factura_email || "",
    factura_codigo_postal: r.factura_codigo_postal || "",
  };
}

function ResellerProfileFields({ value, onChange, idPrefix }) {
  return (
    <div className="border border-violet-100 bg-violet-50/40 rounded-sm p-3 space-y-3">
      <div className="text-[10px] font-semibold text-violet-900 uppercase tracking-wider">Perfil revendedor</div>
      <div>
        <Label className="text-xs">¿Requiere factura?</Label>
        <RadioGroup
          value={value.requires_invoice ? "si" : "no"}
          onValueChange={(v) => onChange({ ...value, requires_invoice: v === "si" })}
          className="flex gap-4 mt-2"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="no" id={`${idPrefix}-fac-no`} />
            <Label htmlFor={`${idPrefix}-fac-no`} className="text-xs font-normal cursor-pointer">
              No
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="si" id={`${idPrefix}-fac-si`} />
            <Label htmlFor={`${idPrefix}-fac-si`} className="text-xs font-normal cursor-pointer">
              Sí
            </Label>
          </div>
        </RadioGroup>
      </div>
      {value.requires_invoice ? (
        <div className="grid grid-cols-1 gap-2">
          <div>
            <Label className="text-[10px]">RFC (facturación)</Label>
            <Input
              value={value.factura_rfc}
              onChange={(e) => onChange({ ...value, factura_rfc: e.target.value.toUpperCase() })}
              className="h-8 mt-1 text-xs font-mono"
            />
          </div>
          <div>
            <Label className="text-[10px]">Razón social</Label>
            <Input
              value={value.factura_razon_social}
              onChange={(e) => onChange({ ...value, factura_razon_social: e.target.value })}
              className="h-8 mt-1 text-xs"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px]">Correo fiscal</Label>
              <Input
                type="email"
                value={value.factura_email}
                onChange={(e) => onChange({ ...value, factura_email: e.target.value })}
                className="h-8 mt-1 text-xs"
              />
            </div>
            <div>
              <Label className="text-[10px]">C.P.</Label>
              <Input
                value={value.factura_codigo_postal}
                onChange={(e) => onChange({ ...value, factura_codigo_postal: e.target.value })}
                className="h-8 mt-1 text-xs"
              />
            </div>
          </div>
        </div>
      ) : null}
      <div>
        <Label className="text-[10px]">Datos generales (contacto, notas)</Label>
        <SpellcheckTextarea
          value={value.datos_generales}
          onChange={(e) => onChange({ ...value, datos_generales: e.target.value })}
          rows={3}
          className="mt-1 rounded-sm text-[11px] resize-none"
          placeholder="Teléfono, domicilio, persona de contacto, acuerdos comerciales…"
        />
      </div>
    </div>
  );
}

/**
 * @param {"distribuidor"|"despacho"} variant
 */
export default function GestionUsuariosEquipo({ variant = "despacho", showResellerUi = false }) {
  const { user } = useAuth();
  const isTrueOwner = !!user?.is_admin_owner;
  const isBuyerTenantAdmin = Boolean(user?.is_buyer_tenant && user?.role === "buyer_admin");
  const isAnyAdmin = isTrueOwner || user?.role === "admin" || isBuyerTenantAdmin;
  const isDistribuidor = variant === "distribuidor";

  const [users, setUsers] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPwd, setResetPwd] = useState("");
  const [resetName, setResetName] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [resetRole, setResetRole] = useState("user");
  const [resetPermitted, setResetPermitted] = useState([]);
  const [busyReset, setBusyReset] = useState(false);
  const [editReseller, setEditReseller] = useState(() => ({ ...EMPTY_RESELLER }));
  const [newReseller, setNewReseller] = useState(() => ({ ...EMPTY_RESELLER }));
  const [createOpen, setCreateOpen] = useState(false);
  const [newU, setNewU] = useState({
    email: "",
    name: "",
    role: isDistribuidor ? "revendedor" : "user",
    password: "",
    permitted_clients: [],
  });
  const [busyCreate, setBusyCreate] = useState(false);

  const loadUsers = async () => {
    try {
      const endpoint = isDistribuidor ? "/admin/users/internal-bnt" : "/admin/users";
      const { data } = await api.get(endpoint);
      setUsers(data || []);
    } catch (e) {
      console.warn("[GestionUsuariosEquipo] No se pudo cargar usuarios:", e?.response?.status);
      setUsers([]);
    }
  };

  const loadAllClients = async () => {
    try {
      const { data } = await api.get("/clients");
      setAllClients(data || []);
    } catch (e) {
      console.warn("[GestionUsuariosEquipo] No se pudo cargar clientes:", e?.response?.status);
    }
  };

  useEffect(() => {
    if (isAnyAdmin) {
      loadUsers();
      if (!isDistribuidor) loadAllClients();
    }
  }, [isAnyAdmin, isDistribuidor]);

  const visibleUsers = useMemo(() => {
    if (!isDistribuidor) return users;
    return filterInternalBntAdminUsers(users);
  }, [users, isDistribuidor]);

  const submitReset = async () => {
    const willChangePwd = !!resetPwd;
    const newName = (resetName || "").trim();
    const newEmail = (resetEmail || "").trim().toLowerCase();
    const willChangeName = newName && newName !== (resetTarget?.name || "");
    const willChangeEmail = newEmail && newEmail !== (resetTarget?.email || "");
    const willChangeRole = resetRole !== (resetTarget?.role || "user");
    const effectivePermitted = resetRole === "revendedor" ? [] : resetPermitted;
    const willChangePermitted =
      JSON.stringify([...effectivePermitted].sort()) !==
      JSON.stringify([...(resetTarget?.permitted_clients || [])].sort());
    const willChangeReseller =
      showResellerUi &&
      resetRole === "revendedor" &&
      JSON.stringify(editReseller) !== JSON.stringify(normalizeReseller(resetTarget?.reseller_profile));

    if (!willChangePwd && !willChangeName && !willChangeEmail && !willChangeRole && !willChangePermitted && !willChangeReseller) {
      toast.error("No hay cambios — modifica al menos un campo");
      return;
    }
    if (willChangePwd && resetPwd.length < 4) {
      toast.error("La contraseña debe tener al menos 4 caracteres");
      return;
    }
    setBusyReset(true);
    try {
      await api.post("/admin/users/reset", {
        user_id: resetTarget.id,
        new_password: willChangePwd ? resetPwd : null,
        new_name: willChangeName ? newName : null,
        new_email: willChangeEmail ? newEmail : null,
        new_role: willChangeRole ? resetRole : null,
        permitted_clients: willChangePermitted ? effectivePermitted : null,
        reseller_profile:
          showResellerUi && resetRole === "revendedor" && willChangeReseller ? editReseller : null,
      });
      toast.success("Usuario actualizado");
      setResetTarget(null);
      setResetPwd("");
      loadUsers();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "Error desconocido");
    } finally {
      setBusyReset(false);
    }
  };

  useEffect(() => {
    if (resetTarget) {
      setResetName(resetTarget.name || "");
      setResetEmail(resetTarget.email || "");
      setResetRole(resetTarget.role || "user");
      setResetPermitted(resetTarget.permitted_clients || []);
      setEditReseller(normalizeReseller(resetTarget.reseller_profile));
      setResetPwd("");
    }
  }, [resetTarget]);

  const deleteUser = async (u) => {
    if (!window.confirm(`¿Eliminar la cuenta de ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`Cuenta ${u.email} eliminada`);
      loadUsers();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error");
    }
  };

  const suspendUser = async (u) => {
    if (!window.confirm(`¿Suspender la cuenta de ${u.email}?`)) return;
    try {
      await api.post(`/admin/users/suspend?user_id=${encodeURIComponent(u.id)}`);
      toast.success(`Cuenta ${u.email} suspendida`);
      loadUsers();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al suspender");
    }
  };

  const submitCreate = async () => {
    if (!newU.email || !newU.name) {
      toast.error("Correo y nombre son obligatorios");
      return;
    }
    if (!newU.password) {
      toast.error("Define una contraseña inicial");
      return;
    }
    if (newU.password.length < 4) {
      toast.error("La contraseña debe tener al menos 4 caracteres");
      return;
    }
    setBusyCreate(true);
    try {
      await api.post("/admin/users/create", {
        email: newU.email.trim().toLowerCase(),
        name: newU.name.trim(),
        role: isBuyerTenantAdmin ? "user" : newU.role,
        password: newU.password,
        permitted_clients: newU.role === "revendedor" ? [] : newU.permitted_clients,
        reseller_profile: showResellerUi && newU.role === "revendedor" ? newReseller : undefined,
      });
      toast.success(`Usuario ${newU.email} creado correctamente`);
      setCreateOpen(false);
      setNewU({
        email: "",
        name: "",
        role: isDistribuidor ? "revendedor" : "user",
        password: "",
        permitted_clients: [],
      });
      setNewReseller({ ...EMPTY_RESELLER });
      loadUsers();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al crear");
    } finally {
      setBusyCreate(false);
    }
  };

  if (!isAnyAdmin) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-xs text-amber-900 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <div>La gestión de usuarios solo está disponible para administradores.</div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="flex items-start justify-between mb-3 gap-3">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2 text-sm" style={{ fontFamily: "'Chivo'" }}>
            <UserCog className="w-4 h-4 shrink-0" />
            {isDistribuidor ? "Equipo del distribuidor" : "Gestión de usuarios"}
          </h3>
          <Button
            onClick={() => setCreateOpen(true)}
            className="h-9 rounded-sm bg-[#3DA873] hover:bg-[#3DA873]/90 text-xs shrink-0"
            data-testid="add-team-user-btn"
          >
            <UserPlus className="w-3.5 h-3.5 mr-1.5" />
            Dar de alta usuario
          </Button>
        </div>
        <p className="text-xs text-slate-500 mb-4 leading-snug">
          {isDistribuidor ? (
            <>
              Alta y edición de cuentas para su operación comercial: <strong>administradores</strong>,{" "}
              <strong>revendedores</strong> y auxiliares. Puede restablecer la <strong>contraseña</strong> desde Editar.
              Los accesos de compradores (portal) se gestionan en <strong>Catálogo de compradores</strong>.
            </>
          ) : (
            <>
              Usuarios <strong>internos del despacho</strong> (administrador interno, auxiliar, consulta).{" "}
              <strong>No son compradores SaaS</strong> del sistema: no solicitan licencia ni se registran en{" "}
              <strong>/registro</strong>. El <strong>Propietario / titular</strong> (`is_admin_owner`) es quien
              contrata BNT y gestiona la licencia en Panel o Configuración.
              {isBuyerTenantAdmin ? (
                <>
                  {" "}
                  <span className="font-semibold text-[#0F2D5C]">Cuenta de comprador:</span> solo puedes dar de alta
                  usuarios auxiliares para tu despacho.
                </>
              ) : null}
              {showResellerUi ? (
                <>
                  {" "}
                  En <span className="font-semibold text-violet-800">modo Distribuidor</span> puedes dar de alta el rol{" "}
                  <span className="font-semibold">Revendedor</span>.
                </>
              ) : null}
            </>
          )}
        </p>
        <div className="border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-xs min-w-[900px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 w-[230px]">
                  Correo
                </th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 w-[140px]">
                  Nombre
                </th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 w-[150px]">
                  Rol
                </th>
                {(showResellerUi || isDistribuidor) && (
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 w-[72px]">
                    Factura
                  </th>
                )}
                {!isDistribuidor && (
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                    Empresas visibles
                  </th>
                )}
                <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 w-[90px]">
                  Clave
                </th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 w-[180px]">
                  Acción
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleUsers.map((u) => {
                const rolLbl = u.is_admin_owner && u.role !== "platform_designer"
                  ? "Propietario"
                  : getWorkspaceRoleLabel(u.role, u);
                const rolCls = u.is_admin_owner && u.role !== "platform_designer"
                  ? "bg-emerald-600 text-white"
                  : getWorkspaceRoleBadgeClass(u.role, u);

                const cliCount = u.clients_count ?? 0;
                let cliText;
                if (u.is_admin_owner || (u.role === "admin" && !u.is_buyer_tenant)) {
                  cliText = "Todas";
                } else if (u.role === "buyer_admin") {
                  cliText = `${cliCount} empresa(s)`;
                } else {
                  cliText = `${cliCount} asignadas`;
                }
                if (u.role === "revendedor") cliText = "—";

                const rp = u.reseller_profile || {};
                const facturaTxt = u.role === "revendedor" ? (rp.requires_invoice ? "Sí" : "No") : "—";

                const roleHint = getUserRoleHint(u);
                const deleteBlock = resolveUserDeleteBlockReason(u, user?.id);
                const showDelete = canShowDeleteButton(u, user?.id);

                return (
                  <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono text-[11px]">
                      {u.email}
                      {roleHint ? (
                        <span className="block text-[9px] text-slate-500 font-sans">{roleHint}</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{u.name || "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-sm ${rolCls}`}>{rolLbl}</span>
                    </td>
                    {(showResellerUi || isDistribuidor) && (
                      <td className="px-3 py-2 text-slate-600 text-[11px] whitespace-nowrap">{facturaTxt}</td>
                    )}
                    {!isDistribuidor && <td className="px-3 py-2 text-slate-600 text-[11px]">{cliText}</td>}
                    <td className="px-3 py-2 text-center">{u.has_password ? "✓" : "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          onClick={() => setResetTarget(u)}
                          className="h-7 text-[11px] rounded-sm bg-amber-500 hover:bg-amber-600 text-white shadow-sm"
                          data-testid={`edit-user-${u.id}`}
                        >
                          <Pencil className="w-3 h-3 mr-1" /> Modificar datos
                        </Button>
                        {showDelete ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => deleteUser(u)}
                            className="h-7 text-[11px] rounded-sm border-rose-300 text-rose-700 hover:bg-rose-50"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        ) : deleteBlock ? (
                          <span className="text-[9px] text-slate-500 max-w-[120px] text-right" title={deleteBlock}>
                            No se puede borrar
                          </span>
                        ) : null}
                        {u.status !== "suspended" && !deleteBlock?.includes("protegido") ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => suspendUser(u)}
                            className="h-7 text-[11px] rounded-sm"
                          >
                            Suspender
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!visibleUsers.length && (
            <p className="text-center text-xs text-slate-500 py-6">No hay usuarios registrados.</p>
          )}
        </div>
      </div>

      <Dialog open={!!resetTarget} onOpenChange={(v) => !v && setResetTarget(null)}>
        <DialogContent className="max-w-lg rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-4 h-4 text-amber-600" /> Editar usuario
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-[#0F2D5C] text-white rounded-sm p-3 flex items-start gap-3">
              <UserCog className="w-6 h-6 text-[#3DA873] mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-slate-300 font-semibold">Editando a</div>
                <div className="font-mono text-sm font-bold mt-0.5 truncate">{resetTarget?.email}</div>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label className="text-xs">Nombre del usuario</Label>
                <Input value={resetName} onChange={(e) => setResetName(e.target.value)} className="mt-1 rounded-sm" />
              </div>
              <div>
                <Label className="text-xs">Correo electrónico</Label>
                <Input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="mt-1 rounded-sm font-mono"
                  disabled={resetTarget?.is_buyer_tenant}
                />
              </div>
              <UserRoleField
                targetUser={resetTarget}
                value={resetRole}
                onChange={setResetRole}
                showReseller={showResellerUi || isDistribuidor}
                isBuyerTenantAdmin={isBuyerTenantAdmin}
              />
              {!isDistribuidor && !resetTarget?.is_admin_owner && !resetTarget?.is_buyer_tenant ? (
                <p className="text-[10px] text-slate-600 m-0 leading-snug" data-testid="internal-roles-help">
                  Estos roles son internos del despacho. «Administrador» aquí <strong>no</strong> es comprador SaaS ni
                  titular de licencia. Para registrar este correo como comprador, elimine primero la cuenta interna
                  (icono papelera) y use <strong>/registro</strong>.
                </p>
              ) : null}
              {isTrueOwner && !isBuyerTenantAdmin && !resetTarget?.is_admin_owner && resetRole !== "revendedor" && (
                <div className="flex items-center gap-2 pt-1">
                  <Checkbox
                    id="make-owner"
                    checked={resetRole === "admin"}
                    onCheckedChange={(checked) => setResetRole(checked ? "admin" : "user")}
                  />
                  <Label htmlFor="make-owner" className="text-xs text-slate-600 cursor-pointer">
                    Rol administrador interno (acceso amplio al despacho — no es titular de licencia)
                  </Label>
                </div>
              )}
            </div>

            {!isDistribuidor && resetRole === "user" && (
              <div className="border-t border-slate-100 pt-3">
                <Label className="text-xs font-semibold text-slate-700 mb-2 block">Empresas permitidas</Label>
                <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-sm p-2 space-y-2">
                  {allClients.map((c) => (
                    <div key={c.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`edit-perm-${c.id}`}
                        checked={resetPermitted.includes(c.id)}
                        onCheckedChange={(checked) => {
                          if (checked) setResetPermitted([...resetPermitted, c.id]);
                          else setResetPermitted(resetPermitted.filter((id) => id !== c.id));
                        }}
                      />
                      <Label htmlFor={`edit-perm-${c.id}`} className="text-[11px] text-slate-600 cursor-pointer truncate">
                        {c.razon_social}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(showResellerUi || isDistribuidor) && resetRole === "revendedor" && (
              <ResellerProfileFields value={editReseller} onChange={setEditReseller} idPrefix="edit" />
            )}

            <div className="border-t border-slate-100 pt-3">
              <Label className="text-xs">Nueva contraseña (opcional)</Label>
              <Input
                type="password"
                value={resetPwd}
                onChange={(e) => setResetPwd(e.target.value)}
                className="mt-1 rounded-sm font-mono"
                placeholder="mín. 4 caracteres"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setResetTarget(null);
                setResetPwd("");
              }}
              className="rounded-sm"
            >
              Cancelar
            </Button>
            <Button onClick={submitReset} disabled={busyReset} className="rounded-sm bg-amber-500 hover:bg-amber-600 text-white">
              {busyReset && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />} Guardar cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#3DA873]" /> Nuevo usuario
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Correo electrónico</Label>
              <Input
                type="email"
                value={newU.email}
                onChange={(e) => setNewU((s) => ({ ...s, email: e.target.value }))}
                className="mt-1 rounded-sm font-mono"
              />
            </div>
            <div>
              <Label className="text-xs">Nombre completo</Label>
              <Input
                value={newU.name}
                onChange={(e) => setNewU((s) => ({ ...s, name: e.target.value }))}
                className="mt-1 rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Rol</Label>
              {!isBuyerTenantAdmin ? (
                <Select
                  value={newU.role}
                  onValueChange={(v) =>
                    setNewU((s) => ({
                      ...s,
                      role: v,
                      permitted_clients: v === "revendedor" || v === "consulta" ? [] : s.permitted_clients,
                    }))
                  }
                >
                  <SelectTrigger className="mt-1 rounded-sm h-9 text-xs">
                    <SelectValue placeholder="Seleccione un rol" />
                  </SelectTrigger>
                  <SelectContent>
                    {getAssignableWorkspaceRoleOptions({
                      showReseller: showResellerUi || isDistribuidor,
                    }).map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="mt-2 text-xs text-slate-600 rounded-sm border border-slate-100 bg-slate-50 px-3 py-2">
                  Solo puedes dar de alta <strong>auxiliares contables</strong> para tu despacho.
                </p>
              )}
            </div>

            {!isDistribuidor && (isBuyerTenantAdmin || newU.role === "user") && (
              <div className="border-t border-slate-100 pt-3">
                <Label className="text-xs font-semibold text-slate-700 mb-2 block">Empresas permitidas</Label>
                <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-sm p-2 space-y-2">
                  {allClients.map((c) => (
                    <div key={c.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`new-perm-${c.id}`}
                        checked={(newU.permitted_clients || []).includes(c.id)}
                        onCheckedChange={(checked) => {
                          const current = newU.permitted_clients || [];
                          if (checked) setNewU({ ...newU, permitted_clients: [...current, c.id] });
                          else setNewU({ ...newU, permitted_clients: current.filter((id) => id !== c.id) });
                        }}
                      />
                      <Label htmlFor={`new-perm-${c.id}`} className="text-[11px] text-slate-600 cursor-pointer truncate">
                        {c.razon_social}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(showResellerUi || isDistribuidor) && newU.role === "revendedor" && (
              <ResellerProfileFields value={newReseller} onChange={setNewReseller} idPrefix="new" />
            )}

            <div className="border-t border-slate-100 pt-3">
              <Label className="text-xs">Contraseña inicial</Label>
              <Input
                type="password"
                value={newU.password}
                onChange={(e) => setNewU((s) => ({ ...s, password: e.target.value }))}
                className="mt-1 rounded-sm font-mono"
                placeholder="mín. 4 caracteres"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="rounded-sm">
              Cancelar
            </Button>
            <Button onClick={submitCreate} disabled={busyCreate} className="rounded-sm bg-[#3DA873] hover:bg-[#3DA873]/90">
              {busyCreate && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />} Crear usuario
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
